import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';

import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { queryClient } from './lib/queryClient';

// 인기 계정 데이터 미리 로드 (바로 톡)
setTimeout(() => {
  // 재시도 함수 구현
  const fetchWithRetry = async (fetchFn: () => Promise<any>, maxRetries = 3, delay = 1000) => {
    let lastError: Error | null = null;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await fetchFn();
      } catch (err) {
        lastError = err as Error;
        console.warn(`Fetch attempt ${attempt + 1}/${maxRetries} failed:`, err);
        
        // 마지막 시도가 아니면 지연 후 재시도
        if (attempt < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, delay * (attempt + 1)));
        }
      }
    }
    
    throw lastError;
  };

  queryClient.prefetchQuery({
    queryKey: ['publicInstagramAccounts'],
    queryFn: async () => {
      try {
        const { supabase } = await import('./lib/supabase');
        
        // 재시도 로직 적용
        return await fetchWithRetry(async () => {
          const { data, error } = await supabase
            .from('public_instagram_accounts')
            .select('*')
            .not('supabase_latest_post_url', 'is', null)
            .order('updated_at', { ascending: false });
          
          if (error) {
            console.error("Supabase error fetching accounts:", error);
            throw new Error(`Supabase error: ${error.message || error.code || 'Unknown error'}`);
          }
          
          const filteredAccounts = (data || []).filter(account => {
            const followers = account.profile_data?.followersCount || 0;
            const hasImagekitImage = 
              (account.supabase_latest_post_url && account.supabase_latest_post_url.includes('imagekit.io')) || 
              (account.supabase_profile_pic_url && account.supabase_profile_pic_url.includes('imagekit.io'));
            
            return followers >= 10000 && hasImagekitImage;
          });
          
          // 중복 계정 제거 (username 기준으로 하나만 유지)
          const uniqueAccounts: Record<string, any> = {};
          for (const account of filteredAccounts) {
            if (!uniqueAccounts[account.username]) {
              uniqueAccounts[account.username] = account;
            }
          }
          
          return Object.values(uniqueAccounts);
        });
      } catch (e) {
        console.error("Error in prefetch query:", e);
        // 오류 발생해도 빈 배열 반환하여 앱이 중단되지 않도록 함
        return [];
      }
    },
    staleTime: 5 * 60 * 1000, // 5분 캐시
  });
}, 2000); // 페이지 로드 후 지연 실행

// LocalStorage Persister (가볍게 적용). 대용량이면 localforage 사용
const persister = createSyncStoragePersister({
  storage: window.localStorage,
});

createRoot(document.getElementById('root')!).render(
  <PersistQueryClientProvider
    client={queryClient}
    persistOptions={{ persister }}
  >
    <App />
  </PersistQueryClientProvider>
);